'use client';

import { FormField } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { DepositLimits } from '../../../DepositLimits';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { useStepValidation, useValidationErrorHandler } from '../hooks/usePlayerValidation';
import type { DepositLimitsStepProps, FormValues } from '../types';
import { transformFormDataToApiPayload } from '../utils/payloadTransformer';
import { validateFields } from '../utils/stepValidationUtils';

/**
 * Transforms the registration form data for deposit limits validation
 * This uses the shared transformFormDataToApiPayload which now includes noLimit logic
 */
function transformDepositLimitsPayload(formData: FormValues): Record<string, unknown> {
  return transformFormDataToApiPayload(formData);
}

function DepositLimitsStep({
  depositLimits,
  onSuccessStep = 'processing',
  onSuccessStatus = 'success',
  onSuccessCallback,
  showDoItLater = true,
  onBackStep,
  onRegisterBackHandler,
  layoutClassName,
  transformPayload,
  validateWithApi = true,
  showBackButton = true,
  showLogo = true,
  dataTrackFeature,
  dataTrackContainer,
}: DepositLimitsStepProps) {
  const t = useTranslations('form');
  const step = useTranslations('register.screens.depositLimits');
  const form = useFormContext<FormValues>();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = form;
  const { skipToStep, setIsLoading, isLoading, updateFormData, setStatus } = useMultistepForm();
  const lastValidationStatusCode = useRef<number | undefined>(undefined);
  const lastValidationCaughtError = useRef<unknown>(undefined);

  const effectiveTransformPayload = transformPayload ?? ((formData: FormValues): Record<string, unknown> => transformDepositLimitsPayload(formData));

  const { validateWithStatus, isValidating: isApiValidating } = useStepValidation({
    form,
    fieldMapping: {},
    transformPayload: effectiveTransformPayload,
  });

  const { handleError } = useValidationErrorHandler();

  useEffect(() => {
    if (onBackStep && onRegisterBackHandler) {
      const backHandler = () => {
        skipToStep(onBackStep);
        return true;
      };
      onRegisterBackHandler(backHandler);
    }
  }, [onBackStep, onRegisterBackHandler, skipToStep]);

  const validateAndUpdateData = async () => {
    const fieldNames = ['depositLimits.amount' as const, 'depositLimits.period' as const];
    const isFormValid = await validateFields(form, fieldNames);

    if (!isFormValid) {
      return false;
    }

    if (validateWithApi) {
      const {
        success: isApiValid,
        statusCode,
        caughtError,
      } = await validateWithStatus({}, undefined, () => {
        const depositLimitData = getValues('depositLimits');
        updateFormData('depositLimits', depositLimitData);
      });

      lastValidationStatusCode.current = statusCode;
      lastValidationCaughtError.current = caughtError;

      if (!isApiValid) {
        return false;
      }
    }

    return true;
  };

  const handleSetDepositLimit = async () => {
    setIsLoading(true);
    try {
      const isValid = await validateAndUpdateData();
      if (!isValid) {
        handleError(lastValidationStatusCode.current, lastValidationCaughtError.current);
        return;
      }
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
      setStatus(onSuccessStatus);
      skipToStep(onSuccessStep);
    } catch {
      // Swallow errors - loading state reset in finally
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormField
      control={control}
      name="depositLimits"
      render={({ field }) => (
        <DepositLimits
          value={field.value}
          onPeriodChange={async option => {
            setValue('depositLimits', { ...field.value, period: option }, { shouldValidate: false, shouldDirty: true });
            if (!field.value.amount) {
              return;
            }
            await trigger('depositLimits.amount');
          }}
          onAmountChange={amount => field.onChange({ ...field.value, amount })}
          onAmountBlur={async () => {
            field.onBlur();
            await trigger('depositLimits.amount');
          }}
          depositLimits={depositLimits}
          amountError={errors?.depositLimits?.amount?.message}
          fieldIdPrefix="depositLimits"
          onApply={handleSetDepositLimit}
          onDoItLater={() => {
            setValue('depositLimits.amount', -1 as unknown as string);
            setValue('depositLimits.period', { label: t('buttons.monthly'), value: 0 });
            setStatus(onSuccessStatus);
            skipToStep(onSuccessStep);
          }}
          isLoading={isLoading}
          isApiValidating={isApiValidating}
          showDoItLater={showDoItLater}
          stepLayout={{
            title: step('title'),
            description: step('subtitle') || undefined,
            'data-testid': 'deposit-limit-step',
            showLogo,
            showBackButton,
            className: layoutClassName,
            dataTrackFeature,
            dataTrackContainer,
          }}
        />
      )}
    />
  );
}

export { DepositLimitsStep, transformDepositLimitsPayload };
